import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Button from "../componentLibrary/Button";
import { LEGAL_COMPLIANCE_MESSAGES } from "../utils/legal";
import { LegalComplianceStatus } from "../api/types";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import TermsOfServiceModal from "./TermsofServiceModal";
import EULAModal from "./EULAModal";

interface LegalAcceptanceModalProps {
  open: boolean;
  complianceStatus: LegalComplianceStatus;
  onAccept: (
    emailAccountIssues: boolean,
    emailFeedbackRequests: boolean,
    emailNewsUpdates: boolean,
  ) => void;
  onDecline: () => void;
  loading?: boolean;
}

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
  open,
  complianceStatus,
  onAccept,
  onDecline,
  loading = false,
}) => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showEULAModal, setShowEULAModal] = useState(false);
  const [emailAccountIssues, setEmailAccountIssues] = useState(true); // Pre-checked
  const [emailFeedbackRequests, setEmailFeedbackRequests] = useState(false);
  const [emailNewsUpdates, setEmailNewsUpdates] = useState(false);

  const handleAccept = () => {
    onAccept(emailAccountIssues, emailFeedbackRequests, emailNewsUpdates);
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: { minHeight: "400px" },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="h2">
            Updated Legal Documents
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" paragraph>
              {LEGAL_COMPLIANCE_MESSAGES.DOCUMENTS_UPDATED}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Terms and Conditions, Privacy Policy & End User License Agreement
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              Please review our updated legal documents:
            </Typography>

            <Box sx={{ mb: 2, ml: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                •{" "}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                >
                  Terms and Conditions
                </button>{" "}
                (last updated:{" "}
                {complianceStatus.documentDates.termsLastUpdated.toLocaleDateString()}
                )
                {complianceStatus.documentAcceptanceStatus.termsAccepted ? (
                  <span style={{ color: "green", marginLeft: "8px" }}>
                    You viewed these on:{" "}
                    {complianceStatus.documentAcceptanceStatus.termsAcceptedDate?.toLocaleDateString()}
                  </span>
                ) : (
                  <span
                    className="text-salmon-300 font-bold"
                    style={{ marginLeft: "8px" }}
                  >
                    NEW
                  </span>
                )}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                •{" "}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                >
                  Privacy Policy
                </button>{" "}
                (last updated:{" "}
                {complianceStatus.documentDates.privacyLastUpdated.toLocaleDateString()}
                )
                {complianceStatus.documentAcceptanceStatus.privacyAccepted ? (
                  <span style={{ color: "green", marginLeft: "8px" }}>
                    You viewed these on:{" "}
                    {complianceStatus.documentAcceptanceStatus.privacyAcceptedDate?.toLocaleDateString()}
                  </span>
                ) : (
                  <span
                    className="text-salmon-300 font-bold"
                    style={{ marginLeft: "8px" }}
                  >
                    NEW
                  </span>
                )}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                •{" "}
                <button
                  type="button"
                  onClick={() => setShowEULAModal(true)}
                  className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                >
                  End User License Agreement
                </button>{" "}
                (last updated:{" "}
                {complianceStatus.documentDates.eulaLastUpdated.toLocaleDateString()}
                )
                {complianceStatus.documentAcceptanceStatus.eulaAccepted ? (
                  <span style={{ color: "green", marginLeft: "8px" }}>
                    You viewed these on:{" "}
                    {complianceStatus.documentAcceptanceStatus.eulaAcceptedDate?.toLocaleDateString()}
                  </span>
                ) : (
                  <span
                    className="text-salmon-300 font-bold"
                    style={{ marginLeft: "8px" }}
                  >
                    NEW
                  </span>
                )}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              By clicking &quot;Accept and Continue&quot;, you agree to our
              Terms and Conditions, Privacy Policy, and End User License
              Agreement.
            </Typography>

            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                I agree to receive emails from Psych for Life about:
              </Typography>

              <Box
                sx={{ ml: 2, display: "flex", flexDirection: "column", gap: 0 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailAccountIssues}
                      onChange={(e) => setEmailAccountIssues(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Issues with my account (e.g. security issues)
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailFeedbackRequests}
                      onChange={(e) =>
                        setEmailFeedbackRequests(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Requests for feedback to improve my experience with the
                      app
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailNewsUpdates}
                      onChange={(e) => setEmailNewsUpdates(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      News and updates about the app
                    </Typography>
                  }
                />
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontStyle: "italic" }}
              >
                We will only contact you when it is necessary or adds real value
                to your experience.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={onDecline} variant="secondary" disabled={loading}>
            Exit App
          </Button>
          <Button onClick={handleAccept} loading={loading}>
            Accept and Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nested Modals */}
      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      <TermsOfServiceModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <EULAModal open={showEULAModal} onClose={() => setShowEULAModal(false)} />
    </>
  );
};

export default LegalAcceptanceModal;
